import _isEmpty from 'lodash/isEmpty';
import { is } from 'core-js/core/object';
import { getVoidSpaceWidth } from '../../utils';
import {
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_TOUCH_POINT_WIDTH,
  WALL_SEGMENT_HEIGHT,
  BASE_CABINET_HEIGHT,
  CABINET_GAP_SPACE,
} from '../../../../../store/cabinetVisualizer/selectors';
import { DIRECTION, CABINET_SKU } from '../../../cabinetConstants';

const enableTouchPointsOnBothSides = true;
const standardHeight = BASE_CABINET_HEIGHT;
const standardWallCabinetHeight = WALL_SEGMENT_HEIGHT;
const countertopSpace = CABINET_GAP_SPACE;
export const BASE_FILLER_HEIGHT_ABOVE_FLOOR = 4.5;

export const isBaseFiller = (productType) => {
  return productType === 'BaseFiller';
};

export const getDefaultMinMaxColumns = (anchorSide) => {
  const offset = enableTouchPointsOnBothSides ? DEFAULT_TOUCH_POINT_WIDTH : 0;

  const defaultMinColumn = anchorSide === DIRECTION.LEFT ? offset : 0;
  const defaultMaxColumn = DEFAULT_VIEWPORT_WALLSIZE.Columns - offset;

  return { defaultMinColumn, defaultMaxColumn };
};

export const isBaseProduct = (productType, origin) => {
  if (productType === 'Filler' && origin) {
    return origin.Row > 0
    || origin.Height >= Math.abs(origin.Row) + DEFAULT_VIEWPORT_WALLSIZE.Rows;
  }
  return productType === 'Base' || productType === 'BaseFiller' || productType === 'BaseSpacer';
};

export const isWallProduct = (productType) => {
  return productType === 'Wall' || productType === 'WallFiller' || productType === 'WallSpacer';
};

export const isTallBaseProduct = (productType, cabinetHeight) => {
  return isBaseProduct(productType) && cabinetHeight > standardHeight;
};

export const isTallWallProduct = (productType, cabinetHeight, origin) => {
  return !isBaseProduct(productType, origin) && cabinetHeight > standardWallCabinetHeight;
};
export const isShortWallProduct = (productType, cabinetHeight) => {
  return isWallProduct(productType) && cabinetHeight < standardWallCabinetHeight;
};

export const isPantryProduct = (productType, cabinetHeight) => {
  return isBaseProduct(productType) && cabinetHeight > standardHeight + countertopSpace;
};

export const getAdjustedCabinetOriginForEdit = ({
  productType, origin, cabinet, products, direction, cabinetInfo
}) => {

  const voidSpaceWidth = getVoidSpaceWidth(cabinetInfo?.cornerCabinetType);

  const { defaultMinColumn, defaultMaxColumn } = getDefaultMinMaxColumns(direction);
  const adjustedOrigin = { ...origin };
  const otherProducts = products.filter((product) => product.id !== cabinet.id);

  const maxHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows;
  const maxWidth = DEFAULT_VIEWPORT_WALLSIZE.Columns;

  if (isBaseProduct(productType)) {
    adjustedOrigin.Row = isBaseFiller(cabinet.productType)
      ? maxHeight - origin.Height - BASE_FILLER_HEIGHT_ABOVE_FLOOR : maxHeight - origin.Height;
  } else if (isTallWallProduct(productType, origin.Height)) {
    adjustedOrigin.Row = standardWallCabinetHeight - origin.Height;
  } else {
    adjustedOrigin.Row = 0;
  }

  const isBase = isBaseProduct(productType, cabinet.Origin);
  const isWall = isWallProduct(productType) || isPantryProduct(productType, cabinet.Origin.Height);

  if (isBase) {
    adjustedOrigin.Row = isBaseFiller(productType)
      ? maxHeight - origin.Height - BASE_FILLER_HEIGHT_ABOVE_FLOOR : maxHeight - origin.Height;

  } else if (isTallWallProduct(productType, origin.Height, origin)) {
    adjustedOrigin.Row = standardWallCabinetHeight - origin.Height;
  } else {
    adjustedOrigin.Row = 0;
  }

  const otherTargetProducts = otherProducts
    .filter((product) => (isBaseProduct(product.productType, product.Origin) === isBase
  || (isWallProduct(product.productType)
  || isPantryProduct(product.productType, product.Origin.Height)) === isWall));

  const hasCabinetOnLeft = otherTargetProducts
    .some((product) => product.Origin.Column < cabinet.Origin.Column
    && product.Sku !== CABINET_SKU.VOID_SPACE);

  const hasCabinetOnRight = otherTargetProducts
    .some((product) => product.Origin.Column > cabinet.Origin.Column
    && product.Sku !== CABINET_SKU.VOID_SPACE);

  // Wall or Base cabinet positioning
  if (direction === DIRECTION.RIGHT && !_isEmpty(otherTargetProducts)) {
    adjustedOrigin.Column = cabinet.Origin.Column + (cabinet.Origin.Width - origin.Width);
  }

  if (_isEmpty(otherTargetProducts)) {
    adjustedOrigin.Column = direction === DIRECTION.LEFT ? defaultMinColumn : defaultMaxColumn - origin.Width;
  }

  if (cabinetInfo?.cornerCabinet && voidSpaceWidth > 0) {
    if (cabinet.Origin.Column === defaultMinColumn || (!hasCabinetOnLeft && direction === DIRECTION.LEFT)) {
      adjustedOrigin.Column = defaultMinColumn + voidSpaceWidth;
    } else if (!hasCabinetOnRight && direction === DIRECTION.RIGHT) {
      adjustedOrigin.Column = defaultMaxColumn - origin.Width - voidSpaceWidth;
    } else if (direction === DIRECTION.LEFT) {
      adjustedOrigin.Column = cabinet.Origin.Column;
    } else {
      adjustedOrigin.Column = cabinet.Origin.Column + (cabinet.Origin.Width - origin.Width);
    }
  }
  return adjustedOrigin;
};

export const getAdjustedCabinetOrigin = ({
  productType, origin, cabinet, products, direction, cabinetInfo
}) => {
  const voidSpaceWidth = getVoidSpaceWidth(cabinetInfo?.cornerCabinetType);

  const { defaultMinColumn, defaultMaxColumn } = getDefaultMinMaxColumns(direction);
  const adjustedOrigin = { ...origin };
  const otherProducts = products.filter((product) => product.id !== cabinet.id);

  const maxHeight = DEFAULT_VIEWPORT_WALLSIZE.Rows;
  const maxWidth = DEFAULT_VIEWPORT_WALLSIZE.Columns;

  if (isBaseProduct(productType)) {
    adjustedOrigin.Row = isBaseFiller(productType)
      ? maxHeight - origin.Height - BASE_FILLER_HEIGHT_ABOVE_FLOOR : maxHeight - origin.Height;
  } else if (isTallWallProduct(productType, origin.Height)) {
    adjustedOrigin.Row = standardWallCabinetHeight - origin.Height;
  } else {
    adjustedOrigin.Row = 0;
  }

  const wallProducts = otherProducts
    .filter((product) => isWallProduct(product.productType)
    || (isTallBaseProduct(product.productType, product.Origin.Height)
    && product.Origin.Height + origin.Height > maxHeight));

  const baseProducts = otherProducts
    .filter((product) => isBaseProduct(product.productType, product.Origin)
    && product.Origin.Width > 0);

  const defaultColumn = direction === DIRECTION.LEFT ? defaultMinColumn : defaultMaxColumn;
  const wallLeftMostColumn = wallProducts.length > 0
    ? Math.min(...wallProducts.map((cab) => cab.Origin.Column))
    : defaultColumn;
  const wallRightMostColumn = wallProducts.length > 0
    ? Math.max(...wallProducts.map((cab) => cab.Origin.Column + cab.Origin.Width))
    : defaultColumn;

  const baseLeftMostColumn = baseProducts.length > 0
    ? Math.min(...baseProducts.map((cab) => cab.Origin.Column))
    : defaultColumn;
  const baseRightMostColumn = baseProducts.length > 0
    ? Math.max(...baseProducts.map((cab) => cab.Origin.Column + cab.Origin.Width))
    : defaultColumn;

  // Wall or Base cabinet positioning
  if (isWallProduct(productType)) {
    if (_isEmpty(wallProducts)) {
      if (direction === DIRECTION.LEFT) {
        adjustedOrigin.Column = defaultMinColumn;
      } else {
        adjustedOrigin.Column = defaultMaxColumn - origin.Width;
      }
    } else {
      adjustedOrigin.Column = direction === DIRECTION.RIGHT
        ? wallRightMostColumn
        : wallLeftMostColumn - origin.Width;
    }
  } else if (isBaseProduct(productType)) {
    if (_isEmpty(baseProducts)) {
      if (direction === DIRECTION.LEFT) {
        adjustedOrigin.Column = defaultMinColumn;
      } else {
        adjustedOrigin.Column = defaultMaxColumn - origin.Width;
      }
    } else {
      adjustedOrigin.Column = direction === DIRECTION.RIGHT
        ? baseRightMostColumn
        : baseLeftMostColumn - origin.Width;
    }
  }

  if (isBaseProduct(productType) && voidSpaceWidth > 0) {
    adjustedOrigin.Column = direction === DIRECTION.LEFT
      ? adjustedOrigin.Column + (_isEmpty(baseProducts) ? voidSpaceWidth : 0)
      : adjustedOrigin.Column - (_isEmpty(baseProducts) ? voidSpaceWidth : 0);
  }

  return adjustedOrigin;
};
